import React, { useState } from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';
import { AdminStatCard } from './components/AdminStatCard';

// ============================================================================
// Types
// ============================================================================

export interface EmailLog {
  id: number;
  recipient: string;
  subject: string;
  mailerClass: string;
  mailerAction: string;
  status: 'sent' | 'delivered' | 'bounced' | 'failed' | 'complained';
  sentAt: string | null;
}

export interface AdminEmailLogsPageProps {
  emailLogs: EmailLog[];
  stats: {
    total: number;
    delivered: number;
    bounced: number;
    complained: number;
  };

  // Filter state
  currentStatus: string;
  currentMailerClass: string;
  currentRecipient: string;
  currentDateFrom: string;
  currentDateTo: string;

  // URLs
  dashboardPath: string;
  emailLogsPath: string;
  analyticsPath: string;
  emailLogPath: (id: number) => string;

  // Pagination
  currentPage: number;
  totalPages: number;
  paginationPath: (page: number) => string;

  // Labels
  labels: {
    title: string;
    subtitle: string;
    backLabel: string;
    analyticsButton: string;
    // Stats
    totalEmailsLabel: string;
    deliveredLabel: string;
    bouncedLabel: string;
    complainedLabel: string;
    // Filters
    statusLabel: string;
    statusAll: string;
    statusSent: string;
    statusDelivered: string;
    statusBounced: string;
    statusFailed: string;
    statusComplained: string;
    mailerLabel: string;
    mailerPlaceholder: string;
    recipientLabel: string;
    recipientPlaceholder: string;
    dateFromLabel: string;
    dateToLabel: string;
    filterButton: string;
    clearButton: string;
    // Table
    idColumn: string;
    recipientColumn: string;
    subjectColumn: string;
    mailerColumn: string;
    statusColumn: string;
    sentAtColumn: string;
    viewButton: string;
  };
}

// ============================================================================
// Helper Components
// ============================================================================

function StatusBadge({ status }: { status: EmailLog['status'] }) {
  const configs = {
    delivered: { bg: 'bg-success', label: 'Delivered' },
    bounced: { bg: 'bg-warning', label: 'Bounced' },
    complained: { bg: 'bg-danger', label: 'Complained' },
    failed: { bg: 'bg-danger', label: 'Failed' },
    sent: { bg: 'bg-secondary', label: 'Sent' },
  };

  const config = configs[status] || configs.sent;

  return <span className={`badge ${config.bg}`}>{config.label}</span>;
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminEmailLogsPage({
  emailLogs,
  stats,
  currentStatus,
  currentMailerClass,
  currentRecipient,
  currentDateFrom,
  currentDateTo,
  dashboardPath,
  emailLogsPath,
  analyticsPath,
  emailLogPath,
  currentPage,
  totalPages,
  paginationPath,
  labels,
}: AdminEmailLogsPageProps) {
  const [status, setStatus] = useState(currentStatus);
  const [mailerClass, setMailerClass] = useState(currentMailerClass);
  const [recipient, setRecipient] = useState(currentRecipient);
  const [dateFrom, setDateFrom] = useState(currentDateFrom);
  const [dateTo, setDateTo] = useState(currentDateTo);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (mailerClass) params.set('mailer_class', mailerClass);
    if (recipient) params.set('recipient', recipient);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);

    window.location.href = `${emailLogsPath}?${params.toString()}`;
  };

  return (
    <div className="container-fluid py-4">
      <AdminPageHeader
        title={labels.title}
        subtitle={labels.subtitle}
        icon="envelope"
        backLink={{ href: dashboardPath, label: labels.backLabel }}
        actions={
          <a href={analyticsPath} className="btn btn-primary me-2">
            {labels.analyticsButton}
          </a>
        }
      />

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <AdminStatCard
            title={labels.totalEmailsLabel}
            value={stats.total}
            icon="envelope"
            iconColor="primary"
          />
        </div>
        <div className="col-md-3">
          <AdminStatCard
            title={labels.deliveredLabel}
            value={stats.delivered}
            icon="check-circle"
            iconColor="success"
            borderColor="success"
            valueColor="success"
          />
        </div>
        <div className="col-md-3">
          <AdminStatCard
            title={labels.bouncedLabel}
            value={stats.bounced}
            icon="exclamation-circle"
            iconColor="warning"
            borderColor="warning"
            valueColor="warning"
          />
        </div>
        <div className="col-md-3">
          <AdminStatCard
            title={labels.complainedLabel}
            value={stats.complained}
            icon="x-circle"
            iconColor="danger"
            borderColor="danger"
            valueColor="danger"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleFilter} className="row g-3">
            <div className="col-md-3">
              <label className="form-label">{labels.statusLabel}</label>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">{labels.statusAll}</option>
                <option value="sent">{labels.statusSent}</option>
                <option value="delivered">{labels.statusDelivered}</option>
                <option value="bounced">{labels.statusBounced}</option>
                <option value="failed">{labels.statusFailed}</option>
                <option value="complained">{labels.statusComplained}</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{labels.mailerLabel}</label>
              <input
                type="text"
                className="form-control"
                placeholder={labels.mailerPlaceholder}
                value={mailerClass}
                onChange={(e) => setMailerClass(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">{labels.recipientLabel}</label>
              <input
                type="text"
                className="form-control"
                placeholder={labels.recipientPlaceholder}
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">{labels.dateFromLabel}</label>
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">{labels.dateToLabel}</label>
              <input
                type="date"
                className="form-control"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button type="submit" className="btn btn-primary me-2">
                {labels.filterButton}
              </button>
              <a href={emailLogsPath} className="btn btn-secondary">
                {labels.clearButton}
              </a>
            </div>
          </form>
        </div>
      </div>

      {/* Email Logs Table */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>{labels.idColumn}</th>
                <th>{labels.recipientColumn}</th>
                <th>{labels.subjectColumn}</th>
                <th>{labels.mailerColumn}</th>
                <th>{labels.statusColumn}</th>
                <th>{labels.sentAtColumn}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {emailLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.id}</td>
                  <td>
                    <span
                      className="text-truncate d-inline-block"
                      style={{ maxWidth: 200 }}
                      title={log.recipient}
                    >
                      {log.recipient}
                    </span>
                  </td>
                  <td>
                    <span
                      className="text-truncate d-inline-block"
                      style={{ maxWidth: 300 }}
                      title={log.subject}
                    >
                      {log.subject}
                    </span>
                  </td>
                  <td>
                    <small className="text-muted">
                      {log.mailerClass}#{log.mailerAction}
                    </small>
                  </td>
                  <td>
                    <StatusBadge status={log.status} />
                  </td>
                  <td>{formatDateTime(log.sentAt)}</td>
                  <td>
                    <a href={emailLogPath(log.id)} className="btn btn-sm btn-outline-primary">
                      {labels.viewButton}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <a className="page-link" href={paginationPath(currentPage - 1)}>
                Previous
              </a>
            </li>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                <a className="page-link" href={paginationPath(page)}>
                  {page}
                </a>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <a className="page-link" href={paginationPath(currentPage + 1)}>
                Next
              </a>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}

export default AdminEmailLogsPage;
